// Auto-balance unclosed/mismatched JSX tags without touching content in strings or {…} expressions.
export function healJSX(input: string) {
    const len = input.length;
    let i = 0;
    let result = '';
    let lastCopy = 0;
  
    // Track nesting of open tags
    const stack: string[] = [];
  
    // Global state guards so we don't treat '<' as a tag start when inside
    // strings, template literals, or JSX expression braces { … }.
    let inString = null;        // "'" or '"'
    let inTemplate = false;     // backticks
    let tplBraceDepth = 0;      // ${ … } inside template literals
    let exprBraceDepth = 0;     // { … } in JSX
  
    const isNameChar = (c: string) => /[A-Za-z0-9._:-]/.test(c);
  
    function readTagName(start: number) {
      // start points at first name char
      let j = start;
      while (j < len && isNameChar(input[j])) j++;
      const name = input.slice(start, j);
      return { name, end: j };
    }
  
    function findTagEnd(from: number) {
      // Walk to '>' respecting quotes and {…}
      let j = from, q = null, brace = 0;
      while (j < len) {
        const c = input[j];
        if (q) {
          if (c === '\\') { j += 2; continue; }
          if (c === q) q = null;
          j++; continue;
        }
        if (c === '"' || c === "'") { q = c; j++; continue; }
        if (c === '{') { brace++; j++; continue; }
        if (c === '}') { if (brace) brace--; j++; continue; }
        if (c === '>') return j;
        j++;
      }
      return -1; // not found
    }
  
    function flushInsertAutoClosers(targetName: string) {
      // If we see </X> and the top isn’t X, auto-close until X or empty.
      const idx = stack.lastIndexOf(targetName);
      if (idx === -1) return ''; // stray closer; do nothing
      let s = '';
      for (let k = stack.length - 1; k > idx; k--) s += `</${stack[k]}>`;
      stack.length = idx + 1;
      return s;
    }
  
    while (i < len) {
      const ch = input[i];
  
      // Handle global guards first
      if (inString) {
        if (ch === '\\') { i += 2; continue; }
        if (ch === inString) inString = null;
        i++;
        continue;
      }
      if (inTemplate) {
        if (ch === '\\') { i += 2; continue; }
        if (ch === '`' && tplBraceDepth === 0) { inTemplate = false; i++; continue; }
        if (ch === '{') { tplBraceDepth++; i++; continue; }
        if (ch === '}' && tplBraceDepth > 0) { tplBraceDepth--; i++; continue; }
        i++;
        continue;
      }
      if (ch === '"' || ch === "'") { inString = ch; i++; continue; }
      if (ch === '`') { inTemplate = true; tplBraceDepth = 0; i++; continue; }
  
      if (ch === '{') { exprBraceDepth++; i++; continue; }
      if (ch === '}' && exprBraceDepth > 0) { exprBraceDepth--; i++; continue; }
  
      // Only treat '<' as a tag start when *not* in {…}
      if (ch === '<' && exprBraceDepth === 0) {
        const next = input[i + 1];
  
        // Closing tag
        if (next === '/') {
          const afterSlash = i + 2;
          const { name, end } = readTagName(afterSlash);
          if (!name) { i++; continue; }
          const gt = findTagEnd(end);
          if (gt === -1) { i++; continue; } // bail if malformed
  
          // Copy everything up to the '<' that starts this closer
          result += input.slice(lastCopy, i);
  
          // Insert any required auto-closers before this closer
          result += flushInsertAutoClosers(name);
  
          // Emit the actual closing tag as-is
          result += input.slice(i, gt + 1);
  
          // Pop matching opener if present
          if (stack.length && stack[stack.length - 1] === name) stack.pop();
  
          i = gt + 1;
          lastCopy = i;
          continue;
        }
  
        // Opening tag (component or intrinsic)
        if (/[A-Za-z]/.test(next)) {
          const nameStart = i + 1;
          const { name, end } = readTagName(nameStart);
          if (!name) { i++; continue; }
  
          const gt = findTagEnd(end);
          if (gt === -1) { i++; continue; }
  
          // Determine self-closing by scanning back from '>'
          let j = gt - 1;
          while (j > end && /\s/.test(input[j])) j--;
          const isSelfClosing = input[j] === '/';
  
          // Copy the whole tag through '>'
          result += input.slice(lastCopy, gt + 1);
          lastCopy = gt + 1;
  
          if (!isSelfClosing) stack.push(name);
  
          i = gt + 1;
          continue;
        }
      }
  
      i++;
    }
  
    // Copy any trailing text
    result += input.slice(lastCopy);
  
    // Append any missing closers at EOF
    for (let k = stack.length - 1; k >= 0; k--) {
      result += `</${stack[k]}>`;
    }
  
    return result;
  }
  